import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  updateProfile,
  fetchProfile,
  profileSelector,
  clearError,
  ProfileUpdateData,
  UserProfile,
} from "./profileSlice";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { uploadFileToS3 } from "@/utils/aws";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile | null;
}

// Subjects matching Prayas AI
const SUBJECTS = ["GS1", "GS2", "GS3", "GS4", "ESSAY"];

export const EditProfileDialog = ({
  open,
  onOpenChange,
  profile,
}: EditProfileDialogProps) => {
  const dispatch = useAppDispatch();
  const { isUpdating: isProfileUpdating, isError, errorMessage, isSuccess } =
    useAppSelector(profileSelector);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState<ProfileUpdateData & { target_exam_year?: string | number }>(
    {
      full_name: "",
      target_exam_year: "",
      preferred_subjects: [],
    }
  );

  // Initialize form data when profile changes or dialog opens
  useEffect(() => {
    if (profile && open) {
      setFormData({
        full_name: profile.full_name || "",
        target_exam_year: profile.target_exam_year || "",
        preferred_subjects: profile.preferred_subjects || [],
      });
      setAvatarPreview(profile.avatar || null);
      setSelectedFile(null); // Reset file selection
    }
  }, [profile, open]);

  // Handle success/error messages
  useEffect(() => {
    if (isSuccess) {
      dispatch(fetchProfile());
      onOpenChange(false);
    }
    if (isError && errorMessage) {
      toast.error(errorMessage);
      dispatch(clearError());
    }
  }, [isSuccess, isError, errorMessage, dispatch, onOpenChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubjectToggle = (subject: string) => {
    setFormData((prev) => {
      const currentSubjects = prev.preferred_subjects || [];
      const newSubjects = currentSubjects.includes(subject)
        ? currentSubjects.filter((s) => s !== subject)
        : [...currentSubjects, subject];
      return {
        ...prev,
        preferred_subjects: newSubjects,
      };
    });
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Capture the file for upload
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    let avatarUrl = avatarPreview;

    // Handle File Upload to S3 if a new file is selected
    if (selectedFile) {
      try {
        setIsUploading(true);
        const { fileUrl } = await uploadFileToS3(selectedFile);
        avatarUrl = fileUrl;
        setIsUploading(false);
      } catch (error: any) {
        console.error("Upload failed", error);
        toast.error("Failed to upload image: " + error.message);
        setIsUploading(false);
        return; // Stop saving if upload fails
      }
    }

    const updateData: ProfileUpdateData = {
      full_name: formData.full_name,
      preferred_subjects: formData.preferred_subjects,
      avatar: avatarUrl || undefined,
    };

    if (formData.target_exam_year) {
      const yearValue = typeof formData.target_exam_year === "string"
        ? parseInt(formData.target_exam_year, 10)
        : formData.target_exam_year;
      if (!isNaN(yearValue)) updateData.target_exam_year = yearValue;
    }

    dispatch(updateProfile(updateData));
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name[0].toUpperCase();
  };

  const isProcessing = isUploading || isProfileUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden rounded-3xl border-0 shadow-2xl bg-white">

        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <DialogTitle className="text-xl font-bold text-gray-900">Edit Profile</DialogTitle>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">

          {/* Avatar Section */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-4 border-gray-50 shadow-sm">
                <AvatarImage src={avatarPreview || undefined} className="object-cover" />
                <AvatarFallback className="text-2xl font-bold bg-gray-100 text-gray-500">
                  {getInitials(formData.full_name || profile?.full_name)}
                </AvatarFallback>
              </Avatar>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 p-2 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <p className="text-sm text-gray-400">Tap icon to change photo</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-sm font-semibold text-gray-700">Full Name</Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="John Doe"
                className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_exam_year" className="text-sm font-semibold text-gray-700">Target Exam Year</Label>
              <Input
                id="target_exam_year"
                name="target_exam_year"
                value={String(formData.target_exam_year || "")}
                onChange={handleInputChange}
                placeholder="2025"
                type="number"
                className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 h-11"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700">Focus Subjects</Label>
              <div className="flex flex-wrap gap-2">
                {SUBJECTS.map((subject) => {
                  const isSelected = formData.preferred_subjects?.includes(subject);
                  return (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => handleSubjectToggle(subject)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${isSelected
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                      {subject}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="pt-2 flex gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isProcessing}
              className="flex-1 h-12 rounded-xl text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isProcessing}
              className="flex-1 h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUploading ? "Uploading..." : "Saving..."}
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>

        </form>
      </DialogContent>
    </Dialog>
  );
};
